import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import CustomerAvatar from "./CustomerAvatar";
import {
  FaHistory,
  FaRedoAlt,
  FaSearch,
  FaTimes,
  FaUser,
} from "react-icons/fa";
import { FaWallet } from "react-icons/fa6";
import { useGetCustomerTransactionsQuery } from "../../toolkitServices/customerTransactionApi";

const show = (v, fallback = "—") =>
  v === 0 || v === "0" ? "0" : v ? String(v) : fallback;

const up = (x) => String(x || "").toUpperCase();

const formatDateTime = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "";

  return dt.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatAmount = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const badgeClassesByType = {
  OPENING: "bg-blue-50 text-blue-700 border-blue-100",
  INVOICE: "bg-emerald-50 text-emerald-700 border-emerald-100",
  URD: "bg-orange-50 text-orange-700 border-orange-100",
  PAYMENT: "bg-indigo-50 text-indigo-700 border-indigo-100",
  WALLET: "bg-pink-50 text-pink-700 border-pink-100",
  INVOICE_RETURN: "bg-amber-50 text-amber-700 border-amber-100",
  SALES_RETURN: "bg-amber-50 text-amber-700 border-amber-100",
  REFUND: "bg-rose-50 text-rose-700 border-rose-100",
  SCHEME: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100",
};

const typeLabel = (t, row) => {
  const T = up(t);

  if (!T) return "Transaction";

  if (T === "WALLET") {
    const dir = up(row?.walletDirection);
    if (dir === "CREDIT") return "Wallet Top-up";
    if (dir === "DEBIT") return "Wallet Withdraw";
    return "Wallet";
  }

  switch (T) {
    case "INVOICE":
      return "Invoice";
    case "URD":
      return "URD";
    case "PAYMENT":
      return "Receipt";
    case "OPENING":
      return "Opening";
    case "SCHEME":
      return "Scheme";
    case "SALES_RETURN":
    case "INVOICE_RETURN":
      return "Sales Return";
    case "REFUND":
      return "Refund";
    default:
      return T;
  }
};

const getLedgerAmountValue = (r) => {
  const tokens = String(r?.note || "").split(" ");
  const lastToken = tokens?.[tokens.length - 1] || "";
  const fallbackFromNote = String(lastToken).replace(/[^\d.]/g, "");
  const fallbackNum = fallbackFromNote ? Number(fallbackFromNote) : 0;

  return (
    Number(r?.credit) ||
    Number(r?.invoicePaid) ||
    Number(r?.debit) ||
    (Number.isFinite(fallbackNum) ? fallbackNum : 0)
  );
};

const getLedgerAmountTone = (r) => {
  if (Number(r?.credit || r?.invoicePaid) > 0) return "credit";
  if (Number(r?.debit) > 0) return "debit";
  return "default";
};

export default function CustomerAvatarHoverCard({
  size = "md",
  tone = "violet",
  className = "",
  title = "Customer Detail",
  align = "center",
  info = {},
  customer = {},
}) {
  const [open, setOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const rootRef = useRef(null);

  const customerId =
    customer?.value ||
    customer?._id ||
    customer?.id ||
    info?.customerDbId ||
    info?._id ||
    info?.id;

  const customerName =
    info?.name || customer?.info?.customerName || customer?.label || "";

  const customerCode =
    info?.code || customer?.info?.customerId || customer?.label || "";

  const toggle = () => {
    if (!customerId) return;
    setOpen((s) => !s);
  };

  const close = () => setOpen(false);

  const openHistory = (e) => {
    e?.stopPropagation?.();
    if (!customerId) return;
    setOpen(false);
    setHistoryOpen(true);
  };

  useEffect(() => {
    if (!customerId) {
      setOpen(false);
      setHistoryOpen(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) close();
    };

    const onKey = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const alignCls =
    align === "left"
      ? "left-0"
      : align === "right"
        ? "right-0"
        : "left-1/2 -translate-x-1/2";

  const outAmt = Number(info?.outstandingAmount ?? 0);
  const outTone =
    Number.isFinite(outAmt) && outAmt > 0 ? "danger" : "amount";

  return (
    <span ref={rootRef} className="relative inline-block">
      <button
        type="button"
        className="focus-visible:ring-4 focus-visible:ring-violet-400 rounded-2xl outline-none disabled:opacity-60 disabled:cursor-not-allowed"
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={!customerId}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <CustomerAvatar
          size={size}
          tone={tone}
          className={className}
          title={title}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.16 }}
            className={`absolute ${alignCls} mt-2 z-[1000] min-w-[18rem]`}
            role="dialog"
            aria-label="Customer details"
          >
            <span
              className="absolute -top-2 h-4 w-4 rotate-45 right-auto left-0 -translate-x-1/2
                         bg-[#9481ce] border border-slate-200 border-b-0 border-r-0 shadow-sm z-10"
              aria-hidden
            />

            <div className="rounded-2xl border border-slate-200 bg-white shadow-xl overflow-hidden absolute -left-60 md:-left-20">
              <div className="px-3 py-2 bg-[#9481ce] border border-1 border-[#9481ce]">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-white">
                    Customer
                  </span>

                  {info?.code ? (
                    <span className="px-1.5 py-0.5 rounded-md bg-slate-100 text-[10px] text-slate-700">
                      {show(info.code)}
                    </span>
                  ) : null}

                  <div className="ml-auto flex items-center gap-2">
                    {info?.since ? (
                      <span className="text-[10px] text-white">
                        Since {show(info.since)}
                      </span>
                    ) : null}

                    <button
                      type="button"
                      onClick={openHistory}
                      title="Customer transaction history"
                      className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60"
                    >
                      <FaHistory className="text-[12px]" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="px-3 py-2 text-sm text-slate-700">
                <div className="mb-1">
                  <div className="font-semibold text-slate-900 truncate flex gap-2 items-center">
                    <FaUser className="text-slate-400 text-xs" />
                    {show(info?.name, "--")}
                  </div>

                  {(info?.phone || info?.email) && (
                    <div className="text-xs text-slate-500 mt-0.5 space-x-3">
                      {info?.phone && <span>📞 {show(info.phone)}</span>}
                      {info?.email && <span>✉️ {show(info.email)}</span>}
                    </div>
                  )}
                </div>

                {info?.deliveryAddress ? (
                  <div className="text-xs text-slate-600 mt-1">
                    <span className="text-slate-400">📦</span>{" "}
                    <span className="font-semibold text-slate-500 mr-1">
                      Delivery:
                    </span>
                    {show(info.deliveryAddress)}
                  </div>
                ) : null}

                <p className="flex gap-2 items-center px-1 mt-4 mb-1">
                  <FaWallet className="text-lg text-slate-400" />{" "}
                  <span className="text-xs">Customer Ledger</span>
                </p>

                <div className="grid grid-cols-2 gap-2">
                  <Metric
                    label="Fine Gold"
                    value={fmtGram(info?.fineGold)}
                    tone="gold"
                  />
                  <Metric
                    label="Fine Silver"
                    value={fmtGram(info?.fineSilver)}
                    tone="silver"
                  />
                  <Metric
                    label="Wallet / Advance"
                    value={fmtMoney(info?.walletAmount)}
                    tone="amount"
                  />
                  <Metric
                    label="Outstanding"
                    value={fmtMoney(info?.outstandingAmount)}
                    tone={outTone}
                  />
                </div>

                <button
                  type="button"
                  onClick={openHistory}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-[#6b59c6] hover:bg-violet-100 hover:border-violet-300 transition"
                >
                  <FaHistory className="text-[12px]" />
                  View Transaction History
                </button>
              </div>

              <div className="px-3 py-2 bg-slate-50 border-t border-slate-200 text-right">
                <button
                  type="button"
                  className="text-xs px-2 py-1 rounded-md border border-slate-300 text-slate-700 hover:bg-white"
                  onClick={close}
                >
                  Close
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {historyOpen && customerId && (
          <CustomerTransactionHistoryModal
            customerId={customerId}
            customerName={customerName}
            customerCode={customerCode}
            onClose={() => setHistoryOpen(false)}
          />
        )}
      </AnimatePresence>
    </span>
  );
}

function CustomerTransactionHistoryModal({
  customerId,
  customerName,
  customerCode,
  onClose,
}) {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [q, setQ] = useState("");

  const transArgs = useMemo(() => {
    const base = {
      page,
      limit,
      customerId,
      from,
      to,
      q,
    };

    return base;
  }, [page, limit, customerId, from, to, q]);

  const {
    data: transRes,
    isLoading,
    isFetching,
    refetch,
  } = useGetCustomerTransactionsQuery(transArgs, {
    skip: !customerId,
  });

  const rawRows = useMemo(() => transRes?.rows || [], [transRes]);

  // Same as your CustomerTransactions page:
  // currently it hides sourceType/type === INVOICE.
  const rows = useMemo(
    () => rawRows.filter((r) => up(r.sourceType || r.type) !== "INVOICE"),
    [rawRows]
  );

  const total = Number(transRes?.total || 0) || 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = oldOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const clearFilters = () => {
    setFrom("");
    setTo("");
    setQ("");
    setPage(1);
  };

  return (
    <motion.div
      className="fixed inset-0 z-[2000] flex items-end sm:items-center justify-center bg-slate-900/55 backdrop-blur-sm px-2 sm:px-4 py-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 24, scale: 0.98 }}
        transition={{ duration: 0.18 }}
        className="w-full sm:max-w-5xl h-[92vh] sm:h-[86vh] bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-label="Customer transaction history"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bg-[#9481ce] text-white px-4 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-white/15">
                <FaHistory className="text-sm" />
              </span>

              <div className="min-w-0">
                <h3 className="text-sm sm:text-lg font-semibold tracking-tight truncate">
                  Transaction History
                </h3>
                <p className="text-[11px] sm:text-xs text-violet-50 truncate">
                  {customerName || "Customer"}{" "}
                  {customerCode ? `· ${customerCode}` : ""}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isFetching && (
              <span className="hidden sm:inline-flex items-center gap-1 rounded-full bg-white/15 px-2 py-1 text-[10px]">
                <Spinner size={10} /> refreshing
              </span>
            )}

            <button
              type="button"
              onClick={() => refetch?.()}
              disabled={isFetching}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-[#6b59c6] hover:bg-violet-50 disabled:opacity-60"
              title="Refresh"
            >
              <FaRedoAlt className="text-xs" />
            </button>

            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white hover:bg-white/25"
              title="Close"
            >
              <FaTimes className="text-xs" />
            </button>
          </div>
        </div>

        <div className="border-b bg-slate-50 px-3 sm:px-4 py-3">
          <div className="grid grid-cols-2 lg:grid-cols-12 gap-2 items-end">
            <div className="col-span-1 lg:col-span-2">
              <label className="text-[11px] font-semibold text-slate-600">
                From
              </label>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setPage(1);
                }}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[11px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>

            <div className="col-span-1 lg:col-span-2">
              <label className="text-[11px] font-semibold text-slate-600">
                To
              </label>
              <input
                type="date"
                value={to}
                onChange={(e) => {
                  setTo(e.target.value);
                  setPage(1);
                }}
                className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-[11px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>

            <div className="col-span-2 lg:col-span-6">
              <label className="text-[11px] font-semibold text-slate-600">
                Search
              </label>
              <div className="relative mt-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 text-xs" />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => {
                    setQ(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Invoice no, note, reference..."
                  className="w-full rounded-xl border border-slate-200 bg-white pl-8 pr-3 py-2 text-[11px] text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-300"
                />
              </div>
            </div>

            <div className="col-span-2 lg:col-span-2 flex gap-2">
              <button
                type="button"
                onClick={clearFilters}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="relative flex-1 min-h-0">
          {isLoading && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70">
              <div className="flex flex-col items-center gap-2 text-xs text-slate-600">
                <Spinner size={24} />
                Loading transactions...
              </div>
            </div>
          )}

          {/* Desktop / tablet table */}
          <div className="hidden md:block h-full overflow-auto">
            <table className="min-w-full text-xs">
              <thead className="sticky top-0 z-10 bg-slate-50 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Type</th>
                  <th className="px-3 py-2 text-left">Ref</th>
                  <th className="px-3 py-2 text-left">Details</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>

              <tbody>
                {!isLoading && rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-3 py-8 text-center text-sm text-slate-400"
                    >
                      No transactions found for this customer.
                    </td>
                  </tr>
                ) : (
                  rows.map((r) => {
                    const t = up(r.sourceType || r.type);
                    const amountValue = getLedgerAmountValue(r);
                    const amountTone = getLedgerAmountTone(r);

                    return (
                      <tr
                        key={r._id || `${r.sourceType}-${r.sourceId}-${r.date}`}
                        className="border-t border-slate-100 hover:bg-violet-50/40"
                      >
                        <td className="px-3 py-2 align-top text-slate-700">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {formatDateTime(
                                r.date || r.invDate || r.createdAt
                              )}
                            </span>
                            {r.branchName && (
                              <span className="text-[10px] text-slate-400">
                                {r.branchName}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2 align-top">
                          <span
                            className={`inline-flex min-w-[104px] items-center justify-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                              badgeClassesByType[t] ||
                              "bg-gray-50 text-gray-600 border-gray-200"
                            }`}
                          >
                            {typeLabel(t, r)}
                          </span>
                        </td>

                        <td className="px-3 py-2 align-top text-slate-700">
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {r.sourceNo ||
                                r.invoiceRef ||
                                r.sourceRef ||
                                "-"}
                            </span>
                            {(r.paymentMode || r.paymentRefNo) && (
                              <span className="text-[10px] text-slate-400">
                                {r.paymentMode || ""}
                                {r.paymentRefNo ? ` · ${r.paymentRefNo}` : ""}
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="px-3 py-2 align-top text-slate-600">
                          {r.note ? (
                            <span className="line-clamp-2">{r.note}</span>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td className="px-3 py-2 align-top text-right">
                          <MoneyPill value={amountValue} tone={amountTone} />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden h-full overflow-y-auto p-3 space-y-2 bg-slate-50/70">
            {!isLoading && rows.length === 0 ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-400">
                No transactions found for this customer.
              </div>
            ) : (
              rows.map((r) => {
                const t = up(r.sourceType || r.type);
                const amountValue = getLedgerAmountValue(r);
                const amountTone = getLedgerAmountTone(r);

                return (
                  <div
                    key={r._id || `${r.sourceType}-${r.sourceId}-${r.date}`}
                    className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <span
                          className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                            badgeClassesByType[t] ||
                            "bg-gray-50 text-gray-600 border-gray-200"
                          }`}
                        >
                          {typeLabel(t, r)}
                        </span>

                        <p className="mt-1 text-[11px] font-semibold text-slate-700">
                          {formatDateTime(r.date || r.invDate || r.createdAt)}
                        </p>

                        {r.branchName && (
                          <p className="text-[10px] text-slate-400">
                            {r.branchName}
                          </p>
                        )}
                      </div>

                      <MoneyPill value={amountValue} tone={amountTone} />
                    </div>

                    <div className="mt-2 rounded-xl bg-slate-50 px-3 py-2">
                      <div className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="text-slate-400">Ref</span>
                        <span className="font-semibold text-slate-700 truncate">
                          {r.sourceNo || r.invoiceRef || r.sourceRef || "-"}
                        </span>
                      </div>

                      {(r.paymentMode || r.paymentRefNo) && (
                        <div className="mt-1 flex items-center justify-between gap-2 text-[11px]">
                          <span className="text-slate-400">Payment</span>
                          <span className="font-medium text-slate-600 truncate">
                            {r.paymentMode || ""}
                            {r.paymentRefNo ? ` · ${r.paymentRefNo}` : ""}
                          </span>
                        </div>
                      )}
                    </div>

                    {r.note ? (
                      <p className="mt-2 text-[11px] leading-5 text-slate-600 line-clamp-3">
                        {r.note}
                      </p>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="sticky bottom-0 z-30 flex items-center justify-between border-t bg-white px-3 py-2">
          <div className="text-[11px] text-slate-500">
            Page <span className="font-semibold">{page}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
            <span className="text-slate-400"> · Total: {total || rows.length}</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-full border px-3 py-1 text-[11px] hover:bg-slate-50 disabled:opacity-50"
              disabled={page <= 1 || isFetching}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>

            <button
              type="button"
              className="rounded-full border px-3 py-1 text-[11px] hover:bg-slate-50 disabled:opacity-50"
              disabled={page >= totalPages || isFetching}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function MoneyPill({ value, tone = "default" }) {
  const toneCls =
    tone === "credit"
      ? "bg-emerald-50 ring-emerald-200 text-emerald-800"
      : tone === "debit"
        ? "bg-rose-50 ring-rose-200 text-rose-800"
        : "bg-slate-50 ring-slate-200 text-slate-800";

  return (
    <span
      className={`inline-flex items-center justify-end gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold tabular-nums tracking-tight ring-1 shadow-sm ${toneCls}`}
      title={`₹ ${formatAmount(value)}`}
    >
      <span className="text-[10px] opacity-70">₹</span>
      {formatAmount(value)}
    </span>
  );
}

function Spinner({ size = 16, className = "" }) {
  return (
    <span
      className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

function Metric({ label, value, tone = "default" }) {
  const tones = {
    gold: {
      card: "bg-gradient-to-br from-amber-50 to-amber-100 ring-1 ring-amber-200",
      label: "text-amber-800",
      value: "text-amber-700",
    },
    silver: {
      card: "bg-gradient-to-br from-slate-50 to-slate-100 ring-1 ring-slate-200",
      label: "text-slate-700",
      value: "text-slate-800",
    },
    amount: {
      card: "bg-gradient-to-br from-emerald-50 to-emerald-100 ring-1 ring-emerald-200",
      label: "text-emerald-800",
      value: "text-emerald-700",
    },
    danger: {
      card: "bg-gradient-to-br from-rose-50 to-rose-100 ring-1 ring-rose-200",
      label: "text-rose-800",
      value: "text-rose-700",
    },
    default: {
      card: "bg-white ring-1 ring-gray-200",
      label: "text-gray-500",
      value: "text-gray-800",
    },
  };

  const t = tones[tone] || tones.default;

  return (
    <div className={`rounded-xl p-3 ${t.card}`}>
      <div className={`text-xs font-medium ${t.label}`}>{label}</div>
      <div className={`mt-1 text-xs font-semibold tracking-tight ${t.value}`}>
        {value}
      </div>
    </div>
  );
}

function fmtGram(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "—";
  return `${n.toFixed(3)} g`;
}

function fmtMoney(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "—";
  return `₹ ${n.toFixed(2)}`;
}